import { Directive, ElementRef, Input, OnDestroy, AfterViewInit, inject } from '@angular/core';

@Directive({
  standalone: true,
  selector: '[finxonePlatformTooltipArrowPosition]',
})
export class TooltipArrowPositionDirective implements AfterViewInit, OnDestroy {
  @Input('finxonePlatformTooltipArrowPosition') triggerElement?: HTMLElement;
  private observer?: ResizeObserver;
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  ngAfterViewInit() {
    if (!this.triggerElement) return;

    // Use ResizeObserver to continuously monitor layout changes
    // which happens during CDK overlay positioning and mobile keyboard appearing
    this.observer = new ResizeObserver(() => this.updateArrowPosition());
    this.observer.observe(this.elementRef.nativeElement);
    this.observer.observe(document.body);

    // Also update on scroll
    window.addEventListener('scroll', this.updateArrowPosition, true);

    // Initial update
    setTimeout(() => this.updateArrowPosition(), 0);
  }

  ngOnDestroy() {
    this.observer?.disconnect();
    window.removeEventListener('scroll', this.updateArrowPosition, true);
  }

  private readonly updateArrowPosition = () => {
    if (!this.triggerElement || !this.elementRef.nativeElement) return;

    // We set the CSS variable on the parent because the pseudo element is on the .finx-tooltip container
    const tooltipContainer = this.elementRef.nativeElement.closest('.finx-tooltip') as HTMLElement;
    if (!tooltipContainer) return;

    const triggerRect = this.triggerElement.getBoundingClientRect();
    const containerRect = tooltipContainer.getBoundingClientRect();

    // Calculate exact center of the trigger relative to the tooltip container
    const relativeX = triggerRect.left + triggerRect.width / 2 - containerRect.left;
    const relativeY = triggerRect.top + triggerRect.height / 2 - containerRect.top;

    // Clamp values so the arrow doesn't detach from the tooltip bounds
    const clampedX = Math.max(10, Math.min(relativeX, containerRect.width - 10));
    const clampedY = Math.max(10, Math.min(relativeY, containerRect.height - 10));

    tooltipContainer.style.setProperty('--arrow-x', `${clampedX}px`);
    tooltipContainer.style.setProperty('--arrow-y', `${clampedY}px`);
  };
}
